import {Injectable} from '@angular/core';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {PaymentTypeCode} from '@app/model/order/dcp-order.model';
import {PlaceOrderRequestBody} from '@app/model/order/dcp-place-order-body.model';
import {CyberSourceService} from '@app/payment/services/cybersource/cyber-source.service';
import {BaseStoreService} from '@app/services/base-store.service';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {DcpOrderEntryService} from '@app/services/dcp-order-entry.service';
import {TransactionTypeCode} from '@app/services/transactionTypes/transaction-type-code';
import {DcpGlobalMessageService, FeatureTogglesService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Cart, OrderEntry} from '@spartacus/cart/base/root';

import {GlobalMessageType, TranslationService} from '@spartacus/core';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {filter, first, map, switchMap, take} from 'rxjs/operators';
import {DelegoRapidpayService} from './delego-rapidpay/delego-rapidpay.service';
import {PaymentProviderService} from './payment-provider.service';
import {PaymentState} from './store/payment-state';
import {DcpStripeService} from './stripe/dcp-stripe.service';

export enum PaymentProvider {
  DELEGO = 'DELEGO',
  STRIPE = 'STRIPE',
  CYBERSOURCE = 'CYBERSOURCE',
  MOCK_STRIPE = 'MOCK_STRIPE',
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpCcPspService extends PaymentProviderService {
  /**
   * Map of currently used in DCP payment service provides,
   * <key> is payment provider id(stored in cart)
   * <value> is implementation of PaymentProviderService,
   *
   * @private
   */
  private paymentProvidersMap: Map<string, PaymentProviderService> = new Map<string, PaymentProviderService>();
  private currentPaymentProviderService: PaymentProviderService;
  private paymentProviderInitialized$ = new BehaviorSubject<boolean>(false);
  // should be enabled only for d2(env for e2e tests)
  private isMockPaymentProviderEnabled: boolean = false;
  private readonly COUNTRY_FOR_DISCLAIMER = 'Romania';
  private currentCartTransactionType: string;

  constructor(
    private delegoRapidPayService: DelegoRapidpayService,
    private stripeService: DcpStripeService,
    private cyberSourceService: CyberSourceService,
    private featureTogglesService: FeatureTogglesService,
    private dcpActiveCartService: DcpActiveCartService,
    private dcpOrderEntryService: DcpOrderEntryService,
    private baseStoreService: BaseStoreService,
    private featureToggleService: FeatureTogglesService,
    private translationService: TranslationService,
    private dcpGlobalMessageService: DcpGlobalMessageService
  ) {
    super();
    this.paymentProvidersMap.set(PaymentProvider.DELEGO, this.delegoRapidPayService);
    this.paymentProvidersMap.set(PaymentProvider.STRIPE, this.stripeService);
    this.paymentProvidersMap.set(PaymentProvider.CYBERSOURCE, this.cyberSourceService);
    this.paymentProvidersMap.set(PaymentProvider.MOCK_STRIPE, this.stripeService);
    this.subscribeToCurrentCartPaymentProvider();
    this.subscribeToMockPaymentProviderFeatureToggle();
  }

  isDelego(paymentProviderId: string): boolean {
    return paymentProviderId?.toUpperCase() === PaymentProvider.DELEGO;
  }

  isStripe(paymentProviderId: string): boolean {
    return paymentProviderId?.toUpperCase() === PaymentProvider.STRIPE;
  }

  /**
   * pspMerchantId is used to open Psp iFrame,
   *
   * @param cart
   */
  setPspMerchantId(cart: Cart): void {
    this.getCurrentPaymentProviderService().setPspMerchantId(cart);
  }

  isAddNewCreditCardComponentOpened(): Observable<boolean> {
    return this.getCurrentPaymentProviderService$().pipe(
      switchMap((service) => service.isAddNewCreditCardComponentOpened())
    );
  }

  isAddNewCreditCardComponentClosed(): Observable<boolean> {
    return this.isAddNewCreditCardComponentOpened().pipe(map((isOpened) => !isOpened));
  }

  /**
   * Opens add new credit card component for given currency
   * e.g. delego iFrame in case of delego payment provider.
   *
   * @param currency
   */
  openAddNewCreditCardComponent(currency: string): void {
    this.getCurrentPaymentProviderService().openAddNewCreditCardComponent(currency);
  }

  closeAddNewCreditCardComponent(): void {
    this.getCurrentPaymentProviderService().closeAddNewCreditCardComponent();
  }

  startCcFlow(): void {
    this.getCurrentPaymentProviderService().startCcFlow();
  }

  convertStateToPlaceOrderPayload(): Observable<PlaceOrderRequestBody> {
    return this.getCurrentPaymentProviderService().convertStateToPlaceOrderPayload();
  }

  saveAuthorizationErrors(state: PaymentState) {
    return this.getCurrentPaymentProviderService().saveAuthorizationErrors(state);
  }

  displayPaymentError(state: PaymentState) {
    return this.getCurrentPaymentProviderService().displayPaymentError(state);
  }

  /**
   * Returns paymentProvider id based on cart.entries[0].data['getSalesArea'].paymentProvider
   * if isMockPaymentProviderEnabled enabled(only for d2/e2e test env), returns MOCK payment provider for STRIPE
   *
   */
  getCurrentCartPaymentProviderId(): Observable<PaymentProvider> {
    return this.dcpActiveCartService.getEntries().pipe(
      first((entries) => !!entries.length),
      untilDestroyed(this),
      map((entries: OrderEntry[]) => {
        this.currentCartTransactionType = entries[0].transactionType;
        const paymentProvider = this.dcpOrderEntryService.getSalesArea(entries[0])?.paymentProvider?.toUpperCase();
        switch (paymentProvider) {
          case PaymentProvider.DELEGO:
            return PaymentProvider.DELEGO;
          case PaymentProvider.STRIPE:
            return this.isMockPaymentProviderEnabled ? PaymentProvider.MOCK_STRIPE : PaymentProvider.STRIPE;
          default:
            return paymentProvider;
        }
      })
    );
  }

  getSupportedPaymentProviders(): string[] {
    return Array.from(this.paymentProvidersMap.keys());
  }

  private subscribeToCurrentCartPaymentProvider(): void {
    this.getCurrentCartPaymentProviderId()
      .pipe(
        untilDestroyed(this),
        filter((ccPaymentProviderId) => !!ccPaymentProviderId)
      )
      .subscribe((ccPaymentProviderId) => {
        this.currentPaymentProviderService = this.paymentProvidersMap.get(ccPaymentProviderId);
        if (this.currentPaymentProviderService) {
          this.paymentProviderInitialized$.next(true);
        } else if (!this.currentCartTransactionType?.includes(TransactionTypeCode.ORDER_UPSELL)) {
          // upsell does not support CC payments
          throw new Error(`Could not get paymentProviderService by given id: "${ccPaymentProviderId}"`);
        }
      });
  }

  /**
   * Returns PaymentProviderService implementation configured for given payment provider id
   *
   * @private
   */
  private getCurrentPaymentProviderService(): PaymentProviderService {
    if (!this.currentPaymentProviderService) {
      throw new Error('Payment provider is not initialized!');
    }
    return this.currentPaymentProviderService;
  }

  private getCurrentPaymentProviderService$(): Observable<PaymentProviderService> {
    return this.isPaymentProviderInitialized().pipe(
      filter(Boolean),
      take(1),
      map(() => this.currentPaymentProviderService)
    );
  }

  private isPaymentProviderInitialized(): Observable<boolean> {
    return this.paymentProviderInitialized$;
  }

  private subscribeToMockPaymentProviderFeatureToggle() {
    this.featureTogglesService
      .isEnabled(UNLEASH_TOGGLES.mockCcEnabled)
      .pipe(untilDestroyed(this))
      .subscribe((enabled) => {
        this.isMockPaymentProviderEnabled = enabled;
      });
  }

  subscribeToStripeDisclaimer() {
    combineLatest([
      this.baseStoreService.getCurrentBaseStore(),
      this.featureToggleService.isEnabled(UNLEASH_TOGGLES.stripeNoPaymentIntent),
      this.getCurrentCartPaymentProviderId(),
      this.dcpActiveCartService.getActiveCart(),
      this.translationService.translate('checkout.digitalNativeCheckout.paymentType.cc.stripePostponedCharge'),
    ])
      .pipe(first(), untilDestroyed(this))
      .subscribe(([baseStore, toggle, paymentProvider, cart, message]) => {
        if (
          toggle &&
          paymentProvider === PaymentProvider.STRIPE &&
          cart.paymentType.code === PaymentTypeCode.CARD &&
          baseStore?.name?.includes(this.COUNTRY_FOR_DISCLAIMER)
        ) {
          this.dcpGlobalMessageService.addMessage(
            {
              message,
              type: GlobalMessageType.MSG_TYPE_INFO,
            },
            this.constructor.name,
            'errorGlobal'
          );
        }
      });
  }
}
